/* eslint-disable indent */
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import Card from '@/components/Card';
import { TFunction } from 'i18next';
import { PhotoWithMenu } from '@/components/Custom/Photo/PhotoWithMenu';
import { useGetAllProcessingsByElementClassId } from '@/hooks/ai.hooks';
import Loader from '@/components/Loading/Loader';
import { ImagePreviewModal } from '@/components/Custom/Modal/ImagePreviewModal';
import { Processing } from '@/api/entities/processing';
import { showModal } from '@/utils/modal.utils';
import { EngineCorrectionModeration } from '@/components/Common/Moderation/EngineCorrectionModeration';
import EngineCorrectionProvider from '@/providers/EngineCorrectionProvider';
import { NotFound } from '@/components/Message/NotFound';
import { EditEquipmentTab } from '@/type/ui/tab/equipmentTab';
import { isLocal, isProd } from '@/utils/constant.utils';
import { useAuth } from '@/hooks/auth.hooks';
import { useInfiniteScroll } from '@/hooks/infinite-scroll.hooks';
import { TEquipmentSchema } from '@/schemas/equipment/equipment.schema';
import { ImgWithYoloBox } from '@/components/Custom/Photo/ImgWithYoloBox';

interface EquipmentPhotoListProps {
  t?: TFunction<undefined>;
  equipment?: TEquipmentSchema;
}

export const EquipmentPhotoList = ({
  t,
  equipment,
}: EquipmentPhotoListProps) => {
  const { id, processingId } = useParams();
  const navigate = useNavigate();
  const { limitedRoleLoggedInUserId } = useAuth();

  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || EditEquipmentTab.DETECTIONS;

  const [selectedProcessing, setSelectedProcessing] = useState<Processing>();
  const [currentPage, setCurrentPage] = useState(1);
  const LIMIT = 40;

  const relatedTo =
    tab === EditEquipmentTab.DETECTIONS ? 'detection' : 'moderation';

  const { data: processings, isLoading: isLoadingProcessings } =
    useGetAllProcessingsByElementClassId(
      relatedTo,
      equipment?.classId,
      currentPage,
      LIMIT,
    );

  const { allItems: allProcessings, observerTarget } =
    useInfiniteScroll<Processing>({
      data: processings?.data?.data,
      isLoading: isLoadingProcessings,
      totalPages: processings?.data?.totalPages,
      currentPage,
      onPageChange: setCurrentPage,
      queryKey: [
        'getAllProcessingsByElementClassId',
        relatedTo,
        equipment?.classId?.toString(),
      ],
      resetTrigger: processingId,
    });

  useEffect(() => {
    if (!(isProd || isLocal) && !limitedRoleLoggedInUserId) {
      navigate('/');
    }
  }, [limitedRoleLoggedInUserId, navigate]);

  // Reset when equipmentName changes
  useEffect(() => {
    if (equipment?.classId) {
      setCurrentPage(1);
    }
  }, [equipment?.classId]);

  const renderImageList = () => {
    return allProcessings.map((item) => {
      const annotationField =
        tab === EditEquipmentTab.DETECTIONS
          ? 'detectionsElements'
          : 'moderationsElements';

      const annotations = item?.[annotationField]?.filter(
        (el) => Number(el?.['elNumber']) === equipment?.classId,
      );

      const imageUrl = item?.imageUrl?.includes('&token=')
        ? `${item?.imageUrl}&processingId=${item?.id}`
        : `${item?.imageUrl}?processingId=${item?.id}`;

      return (
        <PhotoWithMenu
          key={item?.id}
          t={t}
          url={imageUrl}
          urlHasBaseUrl={true}
          manyRectangleAnnotations={
            item?.withOldElementRectangles ? annotations : undefined
          }
          manyPolygonAnnotations={
            !item?.withOldElementRectangles ? annotations : undefined
          }
          onClickImage={() => {
            setSelectedProcessing(item);
            showModal('#equipment-photo-preview-modal');
          }}
          onViewText={
            relatedTo === 'detection'
              ? t('common:viewDetections')
              : t('common:viewModeration')
          }
          onClickView={
            item?.withOldElementRectangles
              ? () => {
                  window.location.href = `/equipment/edit/${id}/processing/${item?.id}?tab=${tab}`;
                }
              : undefined
          }
          onClickModerate={
            item?.withOldElementRectangles
              ? undefined
              : () => {
                  window.location.href = `/equipment/edit/${id}/processing/${item?.id}?tab=${tab}`;
                }
          }
        />
      );
    });
  };

  return (
    <>
      {processingId ? (
        <>
          <EngineCorrectionProvider>
            <EngineCorrectionModeration
              processingId={processingId}
              showOnlyEquipments={true}
              backToLink={`/equipment/edit/${id}?tab=${tab}`}
              showOnlyDetections={tab === EditEquipmentTab.DETECTIONS}
              showOnlyModerations={tab === EditEquipmentTab.MODERATIONS}
              equipmentClassId={equipment?.classId}
            />
          </EngineCorrectionProvider>
        </>
      ) : (
        <Card
          title={
            <TitleSection
              text={
                tab === EditEquipmentTab.DETECTIONS
                  ? t('detectedEquipments')
                  : t('moderatedEquipments')
              }
              defaultIconClassName={
                tab === EditEquipmentTab.DETECTIONS
                  ? 'ki-abstract-25'
                  : 'ki-toggle-on'
              }
            />
          }
          bodyClassName="py-8 flex flex-col gap-5"
        >
          {!isLoadingProcessings &&
          currentPage === 1 &&
          allProcessings.length === 0 ? (
            <div className="pt-4 pb-10">
              <NotFound
                text={`${
                  tab === EditEquipmentTab.DETECTIONS
                    ? t('noDetectedEquipmentsFound')
                    : t('noModeratedEquipmentsFound')
                }...`}
              />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-x-6 gap-y-8 max-1.5xl:grid-cols-3 max-lg:grid-cols-2 max-md:grid-cols-1">
                {renderImageList()}
              </div>
              {/* Sentinel element for infinite scroll - always render when we have items */}
              {allProcessings.length > 0 && (
                <div ref={observerTarget} className="h-10 w-full" />
              )}
              {isLoadingProcessings && currentPage > 1 && (
                <div className="flex justify-center py-4">
                  <Loader />
                </div>
              )}
            </>
          )}
        </Card>
      )}
      <ImagePreviewModal
        id={'equipment-photo-preview-modal'}
        bodyClassName="h-max max-h-[93.5vh] min-[1920px]:h-auto"
        isOpen={selectedProcessing !== undefined}
        onClose={() => {
          setSelectedProcessing(undefined);
        }}
        onClickOutsideContent={() => {
          setSelectedProcessing(undefined);
        }}
        onEscapePress={() => {
          setSelectedProcessing(undefined);
        }}
      >
        {(() => {
          const annotationField =
            tab === EditEquipmentTab.DETECTIONS
              ? 'detectionsElements'
              : 'moderationsElements';

          const annotations = selectedProcessing?.[annotationField]?.filter(
            (el) => Number(el?.['elNumber']) === equipment?.classId,
          );

          return (
            <ImgWithYoloBox
              imgSrc={selectedProcessing?.imageUrl}
              imageClassName="max-h-screen w-auto object-contain object-center"
              rectangleClassName="border-[4px]"
              manyRectangleAnnotations={
                selectedProcessing?.withOldElementRectangles
                  ? annotations
                  : undefined
              }
              manyPolygonAnnotations={
                !selectedProcessing?.withOldElementRectangles
                  ? annotations
                  : undefined
              }
            />
          );
        })()}
      </ImagePreviewModal>
      {isLoadingProcessings && currentPage === 1 && <Loader />}
    </>
  );
};
